import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QueryConstraint, limit as firestoreLimit } from "firebase/firestore";
import { highnote } from "@highnote/server/src/sdk";
import {
  COLLECTION_ID,
  UserEntity,
  Space,
  Track,
  DownloadRequest,
  DropboxSyncJob,
} from "@highnote/server/src/core/entities";
import { ENTITY_NOT_FOUND } from "App/common/util/ERRORS";
import { reportListenerError } from "App/modules/membershipTelemetry";
import { v4 as uuidv4 } from "uuid";

const generateEntitiesWatcher = <EntityType,>(collectionId: COLLECTION_ID) => {
  type EntitiesContextValue = {
    entities: EntityType[];
    loading: boolean;
    error: string;
    totalLimit: number;
    loadMore: () => void;
  };

  const EntitiesContext = createContext<EntitiesContextValue>({
    entities: [],
    loading: true,
    error: undefined,
    totalLimit: 0,
    loadMore: () => undefined,
  });

  const EntitiesContextProvider = ({
    constraints,
    limit = 0,
    children,
  }: {
    constraints: QueryConstraint[];
    limit?: number;
    children: React.ReactNode;
  }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [entities, setEntities] = useState<EntityType[]>([]);
    const [error, setError] = useState<string>();
    const [totalLimit, setTotalLimit] = useState<number>(limit);
    const [retryTrigger, setRetryTrigger] = useState<string>();
    const unmountedRef = useRef<boolean>(false);

    useEffect(
      () => () => {
        unmountedRef.current = true;
      },
      [],
    );

    useEffect(() => {
      if (!constraints || constraints.length < 1) {
        setLoading(false);
        setEntities([]);
        setError(undefined);
        return;
      }

      setLoading(true);
      // A stale error from the previous constraints must not sit next to
      // `loading: true` for the new ones.
      setError(undefined);
      let timeout: number;
      const unsubscribe = highnote.watchEntities({
        collectionId,
        constraints: totalLimit
          ? [...constraints, firestoreLimit(totalLimit)]
          : constraints,
        onChange: (_entities: EntityType[]) => {
          if (unmountedRef.current) return;
          setError(undefined);
          setEntities(_entities);
          setLoading(false);
        },
        onError: (error) => {
          if (unmountedRef.current) return;
          unsubscribe();

          /*
           * The old handler dropped the error argument, and with it the
           * difference between `permission-denied` and `failed-precondition`.
           * A Firestore `list` query fails as a whole, so this is the
           * difference between "a rules regression emptied a surface" and "a
           * composite index is absent". Today both render as a silent empty
           * list.
           */
          reportListenerError(collectionId, error);

          setEntities([]);
          setLoading(false);
          setError(ENTITY_NOT_FOUND);

          /*
           * A `permission-denied` error is terminal: the identical query,
           * re-issued, cannot start to succeed. Without this guard the retry
           * below runs every 5 seconds for the life of the tab.
           * `unavailable` and the other transient codes still retry, because
           * those do recover.
           *
           * The guard is unconditional, and that is the point. It touches no
           * member document and belongs to no migration phase: its one job
           * is to stop the endless retry loop that a denial starts. It used
           * to sit behind a migration flag. That meant the rollback an
           * operator reaches for during a wave of denials was also the
           * switch that turned the retries back on. A safety guard must not
           * be reachable from the switch you pull when the thing it guards
           * against happens.
           */
          if (error?.code === "permission-denied") return;

          // Retry after 5 seconds.
          timeout = window.setTimeout(() => {
            if (unmountedRef.current) return;
            setRetryTrigger(uuidv4());
          }, 5000);
        },
      });

      return () => {
        unsubscribe();
        clearTimeout(timeout);
      };
    }, [collectionId, constraints, totalLimit, retryTrigger]);

    const value = useMemo(
      () => ({
        entities,
        loading,
        error,
        totalLimit,
        loadMore: () => setTotalLimit(totalLimit + limit),
      }),
      [entities, loading, error, totalLimit],
    );

    return (
      <EntitiesContext.Provider value={value}>
        {children}
      </EntitiesContext.Provider>
    );
  };

  const useEntitiesContext = () => useContext(EntitiesContext);

  return {
    EntitiesContextProvider,
    useEntitiesContext,
  };
};

const generateEntityWatcher = <EntityType,>(collectionId: COLLECTION_ID) => {
  type EntityContextValue = {
    entity: EntityType;
    loading: boolean;
    error: string;
  };

  const EntityContext = createContext<EntityContextValue>({
    entity: undefined,
    loading: true,
    error: undefined,
  });

  const EntityContextProvider = ({
    id,
    children,
  }: {
    id: string;
    children: React.ReactNode;
  }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [entity, setEntity] = useState<EntityType>();
    const [error, setError] = useState<string>();
    const [retryTrigger, setRetryTrigger] = useState<string>();
    const retryCountRef = useRef<number>(0);
    const unmountedRef = useRef<boolean>(false);

    useEffect(() => {
      return () => {
        unmountedRef.current = true;
      };
    }, []);

    useEffect(() => {
      setLoading(true);

      if (!id) {
        setEntity(undefined);
        setLoading(false);
        setError(undefined);
        return;
      }

      setError(undefined);
      let timeout: number;
      const unsubscribe = highnote.watchEntity({
        entityId: id,
        collectionId,
        onChange: (_entity: EntityType) => {
          if (unmountedRef.current) return;
          setError(undefined);
          setEntity(_entity);
          setLoading(false);
        },
        onError: (error) => {
          if (unmountedRef.current) return;
          unsubscribe();

          reportListenerError(collectionId, error);

          // Only set the error if we've already missed this twice.
          if (retryCountRef.current > 2) {
            setEntity(undefined);
            setLoading(false);
            setError(ENTITY_NOT_FOUND);
          }

          /*
           * The same terminal guard as in `EntitiesContextProvider` above,
           * and unconditional for the same reason.
           *
           * Warning: set the final state before the early return. Unlike the
           * sibling above, this watcher's `setLoading(false)` sits behind
           * `retryCountRef.current > 2`, and the retries that raise that
           * count are exactly what this guard cancels. On the first
           * `permission-denied` the count is 0, so a bare return here pins
           * `loading` to true for the life of the tab. And `loading` from
           * this provider is `userLoading` in `_AuthContextProvider`, which
           * hides the entire route switch behind `<LoadingScreen />`. One
           * denied read of `users-v1/{uid}` then blanks the whole app on
           * every route, with every membership flag off.
           */
          if (error?.code === "permission-denied") {
            setEntity(undefined);
            setLoading(false);
            // A denied read must read as a FAILURE, not as "no such
            // document" — that is the whole point of surfacing errors.
            setError(ENTITY_NOT_FOUND);
            return;
          }

          // Retry after 5 seconds.
          timeout = window.setTimeout(() => {
            if (unmountedRef.current) return;
            retryCountRef.current += 1;
            setRetryTrigger(uuidv4());
          }, 5000);
        },
      });

      return () => {
        unsubscribe();
        clearTimeout(timeout);
      };
    }, [collectionId, id, retryTrigger]);

    const value = useMemo(() => {
      return {
        entity,
        loading,
        error,
      };
    }, [entity, loading, error]);

    return (
      <EntityContext.Provider value={value}>{children}</EntityContext.Provider>
    );
  };

  const useEntityContext = () => useContext(EntityContext);

  return {
    EntityContextProvider,
    useEntityContext,
  };
};

// The query properties supported for Tracks are:
// - `spaceId`
// - `createdBy`
// - `id`
// To add new query properties, you'll need to amend firestore.rules.
export const {
  EntitiesContextProvider: TracksProvider,
  useEntitiesContext: useTracks,
} = generateEntitiesWatcher<Track>(COLLECTION_ID.TRACK);

export const {
  EntityContextProvider: UserProviderRaw,
  useEntityContext: useUserRaw,
} = generateEntityWatcher<UserEntity>(COLLECTION_ID.USER);

export const {
  EntitiesContextProvider: DownloadRequestsProvider,
  useEntitiesContext: useDownloadRequests,
} = generateEntitiesWatcher<DownloadRequest>(COLLECTION_ID.DOWNLOAD_REQUEST);

export const {
  EntityContextProvider: PublicUserProviderRaw,
  useEntityContext: usePublicUserRaw,
} = generateEntityWatcher<UserEntity>(COLLECTION_ID.PUBLIC_USER);

export const {
  EntityContextProvider: TrackProviderRaw,
  useEntityContext: useTrackRaw,
} = generateEntityWatcher<Track>(COLLECTION_ID.TRACK);

export const {
  EntitiesContextProvider: SpacesProvider,
  useEntitiesContext: useSpaces,
} = generateEntitiesWatcher<Space>(COLLECTION_ID.SPACE);

export const {
  EntitiesContextProvider: DropboxJobsProvider,
  useEntitiesContext: useDropboxJobs,
} = generateEntitiesWatcher<DropboxSyncJob>(COLLECTION_ID.DROPBOX_JOBS);

export const {
  EntityContextProvider: SpaceProviderRaw,
  useEntityContext: useSpaceRaw,
} = generateEntityWatcher<Space>(COLLECTION_ID.SPACE);
